import * as React from "react";
import {injectIntl, InjectedIntlProps, FormattedMessage} from "react-intl";
import moment from "moment";
import {RouteComponentProps} from "react-router";
import {message, Table, Tag, DatePicker, Empty} from "antd";
import {consoleApi} from "../../../apiMiddleware";
import {getCurrentLan} from "@netless/i18n-react-router";
import {LoadingOutlined} from "@ant-design/icons/lib";

export type FinanceBillProps = RouteComponentProps & InjectedIntlProps;
export type FinanceBillStates = {
    billArray: any;
    roomSum: number;
    readOnlySum: number;
    isMaskLoading: boolean;
    staticConvertionSum: number;
    dynamicConvertionSum: number;
    currentMonth: moment.Moment,
};

class FinanceBill extends React.Component<FinanceBillProps, FinanceBillStates> {
    public constructor(props: FinanceBillProps) {
        super(props);
        this.state = {
            billArray: [],
            roomSum: 0,
            readOnlySum: 0,
            isMaskLoading: false,
            staticConvertionSum: 0,
            dynamicConvertionSum: 0,
            currentMonth: moment(),
        };
    }

    private renderMask = (): React.ReactNode => {
        if (this.state.isMaskLoading) {
            return (
                <div className="content-box-mask">
                    <LoadingOutlined style={{fontSize: 36, color: "#3381FF"}}/>
                </div>
            );
        } else {
            return null;
        }
    }

    private sumArr(arr: any[]): void {
        let writableSum = 0;
        let readonlySum = 0;
        let staticConvertionSum = 0;
        let dynamicConvertionSum = 0;
        arr.forEach(ele => {
            if (ele.product === "room") {
                writableSum += ele.amount;
            } else if (ele.product === "room_count_read_only") {
                readonlySum += ele.amount;
            } else if (ele.product === "static_conversion_qps") {
                staticConvertionSum += ele.amount;
            } else if (ele.product === "dynamic_conversion_qps") {
                dynamicConvertionSum += ele.amount;
            }
        });
        this.setState({
            roomSum: Math.floor(writableSum),
            readOnlySum: Math.floor(readonlySum),
            staticConvertionSum: Math.floor(staticConvertionSum),
            dynamicConvertionSum: Math.floor(dynamicConvertionSum),
        });
    }

    private changeMonth = async (date: moment.Moment, dateString: string) => {
        const start = moment(dateString).startOf("month").valueOf();
        const end = moment(dateString).endOf("month").valueOf();
        this.setState({ isMaskLoading: true });
        const res = await consoleApi.data.getBillsList(start, end);
        this.setState({ isMaskLoading: false });
        if (res.code === 200) {
            const billArray = res.data.orders.map((data: any, index: number) => {
                let time = moment(data.startTime).format("YYYY-MM-DD");
                if (data.orderType.priceSource === "dynamic_conversion_qps" || data.orderType.priceSource === "static_conversion_qps") {
                    time = moment(data.startTime).add(1, "day").format("YYYY-MM");
                }
                return {
                    key: `order-${index}`,
                    time: time,
                    product: `${data.orderType.priceSource}`,
                    amount: (data.orderPrice / 100),
                    status: `${data.step}`,
                };
            });
            this.sumArr(billArray);
            this.setState({billArray: billArray});
        }
        this.setState({ currentMonth: date });
    }

    public async componentDidMount(): Promise<void> {
        const timestamp = new Date().getTime();
        const endDay = moment(timestamp).startOf("day").valueOf();
        const startDay = moment(timestamp).startOf("month").valueOf();
        try {
            if (endDay !== startDay) {
                const res = await consoleApi.data.getBillsList(startDay, endDay);
                if (res.code === 200) {
                    const billArray = res.data.orders.map((data: any, index: number) => {
                        let time = moment(data.startTime).format("YYYY-MM-DD");
                        if (data.orderType.priceSource === "dynamic_conversion_qps" || data.orderType.priceSource === "static_conversion_qps") {
                            time = moment(data.startTime).add(1, "day").format("YYYY-MM");
                        }
                        return {
                            key: `order-${index}`,
                            time: time,
                            product: `${data.orderType.priceSource}`,
                            amount: (data.orderPrice / 100),
                            status: `${data.step}`,
                        };
                    });
                    this.sumArr(billArray);
                    this.setState({billArray: billArray});
                }
            }
        } catch (error) {
            message.error(error.message);
        }
    }

    private renderRoomTable(): React.ReactNode {
        const {intl} = this.props;
        const {billArray} = this.state;
        if (billArray && billArray.length > 0) {
            const columns = [
                {
                    title: intl.formatMessage({id: "billing-time"}),
                    dataIndex: "time",
                    key: "time",
                    width: 200,
                },
                {
                    title: intl.formatMessage({id: "order-type"}),
                    dataIndex: "product",
                    key: "product",
                    width: 200,
                    render: (text: any, record: any, index: any): React.ReactNode => {
                        let messageKey = "readonly";
                        let color = "blue";
                        switch (text) {
                            case "room":
                                messageKey = "writable";
                                color = "green";
                                break;
                            case "room_count_read_only":
                               messageKey = "readonly";
                               color = "blue";
                               break;
                            case "static_conversion_qps":
                                messageKey = "static-conversion-qps";
                                color = "geekblue";
                                break;
                            case "dynamic_conversion_qps":
                                messageKey = "dynamic-conversion-qps";
                                color = "purple";
                                break;
                        }
                        return <Tag
                                    key={`product-${index}`}
                                    color={color}>
                                    <FormattedMessage
                                        id={messageKey}
                                    />
                                </Tag>;
                    },
                },
                {
                    title: intl.formatMessage({id: "amount"}),
                    dataIndex: "amount",
                    key: "amount",
                    width: 200,
                },
                {
                    title: intl.formatMessage({id: "billing-status"}),
                    dataIndex: "status",
                    key: "status",
                    width: 200,
                },
            ];
            return (
                <Table
                    pagination={false}
                    bordered
                    style={{width: "100%"}}
                    footer={() => {
                        return <div>
                            <span style={{marginRight: 36}}><FormattedMessage id={"global.writable-sum"}/>：{this.state.roomSum}</span>
                            <span style={{marginRight: 36}}><FormattedMessage id={"global.readonly-sum"}/>：{this.state.readOnlySum}</span>
                            <span style={{marginRight: 36}}><FormattedMessage id={"global.doc-to-image-sum"}/>：{this.state.staticConvertionSum}</span>
                            <span><FormattedMessage id={"global.doc-to-web-sum"}/>：{this.state.dynamicConvertionSum}</span>
                        </div>;
                    }}
                    columns={columns}
                    dataSource={billArray}/>
            );
        } else {
            return <Empty />;
        }
    }

    private renderStatement = (): React.ReactNode => {
        const isEnglish: boolean = getCurrentLan() === "en";
        if (isEnglish) {
            return (
                <div className="console-content-chart-box-tips">
                    <div className="clipboard-innerbox-text-mini">
                        1. If you sign another contract with netless, please refer to your contract for billing details. In addition, a bill will be generated on the 1st of every month based on the usage of the previous month.
                    </div>
                    <div className="clipboard-innerbox-text-mini">
                        2. The system carries out bill deduction on the 6th of every month.
                    </div>
                    <div className="clipboard-innerbox-text-mini">
                        3. After freezing the service, all items will be disabled, but the console can log in and pay normally.
                    </div>
                    <div className="clipboard-innerbox-text-mini">
                        4. The service can be resumed immediately after paying the arrears through Alipay or credit card.
                    </div>
                    <div className="clipboard-innerbox-text-mini">
                        5. Generate bills, bill deductions, balance is too low, freezing and unfreezing will be notified to customers via email or SMS.
                    </div>
                </div>
            );
        } else {
            return (
                <div className="console-content-chart-box-tips">
                    <div className="clipboard-innerbox-text-mini">
                        1. 如果您和 netless 另签合同，账单详情请参照您的合同。除此以外，每月1号根据上月用量产生账单。
                    </div>
                    <div className="clipboard-innerbox-text-mini">
                        2. 每月6号系统执行账单扣费。
                    </div>
                    <div className="clipboard-innerbox-text-mini">
                        3. 冻结服务后，所有项目将被禁用，但是 console 可以正常登录缴费。
                    </div>
                    <div className="clipboard-innerbox-text-mini">
                        4. 通过支付宝或信用卡缴清欠费后可以立刻恢复服务。
                    </div>
                    <div className="clipboard-innerbox-text-mini">
                        5. 产生账单，账单扣费，余额过低，冻结和解冻都会通过邮件或短信的形式通知客户。
                    </div>
                </div>
            );
        }
    }

    public render(): React.ReactNode {

        return (
            <div className="product-center-box">
                <div className="portal-room-outbox">
                    <div className="portal-room-chart">
                        <div className="portal-room-box-mid">
                            <div className="console-content-title-max">
                                <span className="console-content-title-text">
                                    <FormattedMessage id={"billing-statement"}/>
                                </span>
                            </div>
                            <div className="console-content-chart-body">
                                {this.renderStatement()}
                            </div>
                        </div>
                    </div>
                    <div className="portal-room-chart">
                        <div className="portal-room-box-mid">
                            <div className="console-content-title-max">
                                <span className="console-content-title-text">
                                    <FormattedMessage id={"billing-statement"}/>
                                </span>
                                <DatePicker
                                    picker="month"
                                    style={{ marginRight: "10px" }}
                                    onChange={this.changeMonth}
                                    value={this.state.currentMonth}
                                />
                            </div>
                            <div className="console-content-chart-body">
                                <div style={{minHeight: 320, position: "relative"}}
                                     className="console-content-chart-box">
                                    {this.renderMask()}
                                    {this.renderRoomTable()}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}


export default injectIntl(FinanceBill);



// WEBPACK FOOTER //
// ./src/components/portal/finance/FinanceBill.tsx